import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListItem,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import React, { useEffect, useMemo, useState } from "react";
import { classNames } from "react-extras";
import { useTranslation } from "react-i18next";
import { flattenMapLayers } from "~/utils/flattenMapLayers";
import { useAccess } from "~/utils/hooks/useAccess";
import {
  readMapSettings,
  updateMapSettings,
} from "../functions/localMapSettings";
import { LayerGroupTitle } from "../types/layers";
import { CustomMapEventTypes, MapDataTitle } from "../types/map";

const useStyles = makeStyles((theme) => ({
  accordion: {
    backgroundColor: theme.palette.primary.semiLight,
    color: theme.palette.primary.contrastText,
    width: "100%",
  },
  accordionIcon: {
    color: theme.palette.primary.contrastText,
  },
  accordionHeading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  },
  singleChildSummary: {
    display: "none",
  },
  singleChildDetails: {
    padding: 0,
    "& ul": {
      padding: 0,
    },
  },
}));

interface Props {
  title: LayerGroupTitle | MapDataTitle | string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  map?: Map;
}

const SettingsAccordion = ({
  title,
  children,
  defaultOpen = false,
  map,
}: Props): JSX.Element | null => {
  const classes = useStyles();
  const [layerCount, setLayerCount] = useState<number>();
  const [expanded, setExpanded] = useState(
    readMapSettings("accordions", title)?.open ?? defaultOpen,
  );
  const { t } = useTranslation("map");
  const [update, setUpdate] = useState(false);
  const { DamRead, FerryRead } = useAccess();

  useEffect(() => {
    const handler = () => {
      setUpdate((prev) => !prev);
    };
    map?.on(CustomMapEventTypes.SettingsChanged, handler);
    return () => {
      map?.un(CustomMapEventTypes.SettingsChanged, handler);
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;
    const layers = flattenMapLayers(map);
    const filtered = layers.filter(
      (l) =>
        l.get("groupTitle") === title && l.get("hideSettings") === undefined,
    );
    setLayerCount(filtered.length);
  }, [title, map, update]);

  const singleChildLayer = useMemo(
    () => (layerCount ? layerCount === 1 : false),
    [layerCount],
  );

  // Some group titles are handled manually to remove parts of the title if no access to child layers
  const getTitle = React.useCallback(
    (key: Props["title"]): string => {
      const isGroup = Object.values<string>(LayerGroupTitle).includes(key);
      if (!isGroup) return t(key);
      if (key === LayerGroupTitle.DamsAndFerries) {
        if (!DamRead && FerryRead) return t("ferries");
        if (DamRead && !FerryRead) return t("dams");
      }
      return t(key);
    },
    [DamRead, FerryRead, t],
  );

  if (layerCount !== undefined && layerCount === 0) return null;

  return (
    <ListItem>
      <Accordion
        className={classes.accordion}
        expanded={singleChildLayer || expanded}
        onChange={(_, open) => {
          setExpanded(open);
          updateMapSettings({ accordions: { title, open } });
        }}
      >
        <AccordionSummary
          classes={{
            expandIconWrapper: classes.accordionIcon,
            root: classNames({
              [classes.singleChildSummary]: singleChildLayer,
            }),
          }}
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography className={classes.accordionHeading}>
            {getTitle(title)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          classes={{
            root: classNames({
              [classes.singleChildDetails]: singleChildLayer,
            }),
          }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    </ListItem>
  );
};

export default SettingsAccordion;
